import type { Metadata } from "next";
import Link from "next/link";
import { notFound } from "next/navigation";
import { Container } from "@/components/layout/container";
import { prisma } from "@/lib/prisma";
import { AddToCartButton } from "@/components/product/add-to-cart-button";
import Script from "next/script";

type ProductPageProps = {
  params: Promise<{
    slug: string;
  }>;
};

const monoFont = { fontFamily: '"IBM Plex Mono", monospace' };

const pagePalette = {
  background:
    "linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)",
} as React.CSSProperties;

function formatPrice(value: number | string) {
  const amount = typeof value === "string" ? Number(value) : value;
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(amount);
}

function formatStockLabel(stock: number) {
  if (stock <= 0) return "Indisponible";
  if (stock === 1) return "Plus qu’1 en stock";
  if (stock === 2) return "Plus que 2 en stock";
  if (stock === 3) return "Plus que 3 en stock";
  return `${stock} en stock`;
}

export async function generateMetadata({
  params,
}: ProductPageProps): Promise<Metadata> {
  const { slug } = await params;

  const product = await prisma.product.findUnique({
    where: { slug },
    include: {
      images: {
        orderBy: { sortOrder: "asc" },
      },
    },
  });

  if (!product) {
    return {
      title: "Produit introuvable | Jour J Création",
      robots: { index: false, follow: false },
    };
  }

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || "https://jourjcreation.com";
  const url = `${siteUrl}/produit/${slug}`;
  const image = product?.images?.[0]?.imageUrl ?? undefined;

  return {
    title: product.seoTitle || `${product.name} | Jour J Création`,
    description:
      product.seoDescription ||
      product.shortDescription ||
      "Découvrez cette laine teinte artisanalement à la main.",
    alternates: {
      canonical: url,
    },
    openGraph: {
      type: "website",
      url,
      title: product.seoTitle || product.name,
      description:
        product.seoDescription || product.shortDescription || "",
      images: image
        ? [
            {
              url: image,
              width: 1200,
              height: 1200,
              alt: product.name,
            },
          ]
        : undefined,
    },
    twitter: {
      card: "summary_large_image",
      title: product.seoTitle || product.name,
      description:
        product.seoDescription || product.shortDescription || "",
      images: image ? [image] : undefined,
    },
  };
}

export default async function ProductPage({ params }: ProductPageProps) {
  const { slug } = await params;

  const product = await prisma.product.findUnique({
    where: { slug },
    include: {
      category: true,
      images: {
        orderBy: { sortOrder: "asc" },
      },
    },
  });

  if (!product || !product.isPublished) {
    notFound();
  }

  const relatedProducts = await prisma.product.findMany({
    where: {
      isPublished: true,
      categoryId: product.categoryId,
      id: { not: product.id },
    },
    include: {
      images: {
        orderBy: { sortOrder: "asc" },
        take: 1,
      },
    },
    take: 3,
    orderBy: [{ isFeatured: "desc" }, { createdAt: "desc" }],
  });

  const mainImage = product.images[0];

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || "https://jourjcreation.com";
  const productUrl = `${siteUrl}/produit/${product.slug}`;
  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description:
      product.shortDescription || product.description || "",
    image: mainImage?.imageUrl ? [mainImage.imageUrl] : undefined,
    sku: product.id,
    brand: {
      "@type": "Brand",
      name: "Jour J Création",
    },
    offers: {
      "@type": "Offer",
      url: productUrl,
      priceCurrency: "EUR",
      price: Number(product.price),
      availability:
        product.stock > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
    },
  };

  return (
    <div style={pagePalette}>
      <Script
        id={`product-jsonld-${product.id}`}
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productJsonLd) }}
      />
      <ProductHero product={product} mainImage={mainImage} />
      <ProductDetails product={product} />
      <RelatedProducts products={relatedProducts} />
    </div>
  );
}

function ProductHero({
  product,
  mainImage,
}: {
  product: {
    id: string;
    slug: string;
    name: string;
    shortDescription: string | null;
    description: string | null;
    price: unknown;
    compareAtPrice: unknown | null;
    stock: number;
    isGlitter: boolean;
    color: string | null;
    category: { name: string; slug: string };
  };
  mainImage:
    | {
        imageUrl: string;
        alt: string | null;
      }
    | undefined;
}) {
  return (
    <section className="section overflow-hidden pb-8 pt-8 sm:pt-10 lg:pt-12">
      <Container>
        <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-start">
          <div>
            <div className="overflow-hidden rounded-[26px]">
              {mainImage?.imageUrl ? (
                <div className="aspect-[4/5] overflow-hidden rounded-[20px]">
                  <img
                    src={mainImage.imageUrl}
                    alt={mainImage.alt || product.name}
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <div className="aspect-[4/5] rounded-[20px] bg-[linear-gradient(150deg,rgba(209,15,115,0.18),rgba(217,90,47,0.12),rgba(244,215,231,0.90))]" />
              )}
            </div>
          </div>

          <div className="max-w-2xl">
            <p style={monoFont} className="text-[11px] uppercase tracking-[0.22em] text-[var(--accent)]/85">
              {product.category.name}
            </p>

            <h1 className="mt-4 text-5xl leading-[0.96] sm:text-6xl">
              {product.name}
            </h1>

            <p className="mt-6 text-base leading-7 text-[var(--foreground)]/74 sm:text-lg">
              {product.shortDescription ||
                "Une création teinte artisanalement à la main, pensée comme une rencontre entre matière, nuance et émotion visuelle."}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              {product.isGlitter ? (
                <span className="rounded-full bg-[var(--primary)] px-4 py-2 text-xs font-medium text-white">
                  À paillettes
                </span>
              ) : (
                <span className="rounded-full border border-[rgba(217,90,47,0.22)] bg-[rgba(217,90,47,0.12)] px-4 py-2 text-xs font-medium text-[var(--foreground)]/75">
                  Sans paillettes
                </span>
              )}

              {product.color ? (
                <span className="rounded-full border border-[rgba(217,90,47,0.22)] bg-[rgba(217,90,47,0.12)] px-4 py-2 text-xs font-medium text-[var(--foreground)]/75">
                  {product.color}
                </span>
              ) : null}
            </div>

            <div className="mt-10 grid gap-5 rounded-[28px] border border-[var(--border)] bg-[linear-gradient(145deg,rgba(209,15,115,0.14),rgba(217,90,47,0.10))] p-6 shadow-[var(--shadow)] sm:grid-cols-3 sm:items-start">
              <div className="flex h-full flex-col justify-start">
                <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--primary)]/72">
                  Prix
                </p>
                <div className="mt-2 flex min-h-[56px] flex-wrap items-start gap-3">
                  <p className="text-3xl font-semibold leading-none text-[var(--primary)] drop-shadow-[0_8px_18px_rgba(255,63,142,0.10)]">
                    {formatPrice(String(product.price))}
                  </p>
                  {product.compareAtPrice ? (
                    <p className="text-sm font-medium text-[var(--foreground)]/45 line-through">
                      {formatPrice(String(product.compareAtPrice))}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="flex h-full flex-col justify-start pt-[1px]">
                <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--foreground)]/50">
                  Disponibilité
                </p>
                <div className="mt-2 flex min-h-[56px] items-start">
                  <p
                    className={[
                      "text-[1.1rem] font-semibold leading-none sm:text-[1.15rem]",
                      product.stock > 0
                        ? "text-[var(--primary)]"
                        : "text-[var(--foreground)]/75",
                    ].join(" ")}
                  >
                    {formatStockLabel(product.stock)}
                  </p>
                </div>
              </div>

              <div className="flex h-full flex-col justify-start pt-[1px]">
                <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--foreground)]/50">
                  Catégorie
                </p>
                <div className="mt-2 flex min-h-[56px] items-start">
                <p className="text-[1.1rem] font-semibold leading-none text-[var(--accent)]/85 sm:text-[1.15rem]">
                    {product.category.name}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 h-px bg-gradient-to-r from-transparent via-[var(--border)] to-transparent" />
            <div className="mt-8 flex flex-wrap gap-4">
              <AddToCartButton
                product={{
                  id: product.id,
                  slug: product.slug,
                  name: product.name,
                  price: Number(product.price),
                  image: mainImage?.imageUrl ?? null,
                  isGlitter: product.isGlitter,
                  categoryName: product.category.name,
                  stock: product.stock,
                }}
              />
              <Link
                href="/boutique"
                className="rounded-full border border-[rgba(217,90,47,0.22)] bg-[rgba(217,90,47,0.12)] px-6 py-3 text-sm font-medium transition hover:border-[var(--accent)] hover:text-[var(--accent)]"
              >
                Retour à la boutique
              </Link>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

function ProductDetails({
  product,
}: {
  product: {
    description: string | null;
    material: string | null;
    meterage: string | null;
    careInstructions: string | null;
    isGlitter: boolean;
  };
}) {
  return (
    <section className="section pt-0">
      <Container>
        <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="rounded-[30px] border border-[var(--border)] bg-[linear-gradient(145deg,rgba(209,15,115,0.14),rgba(217,90,47,0.10))] p-7 shadow-[var(--shadow)] sm:p-8">
            <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
              À propos de cette création
            </p>

            <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
              Une laine pensée pour offrir présence, douceur et sensibilité colorée.
            </h2>

            <div className="mt-6 space-y-5 text-base leading-7 text-[var(--foreground)]/74">
              <p>
                {product.description ||
                  "Chaque création est teinte à la main dans une recherche d’équilibre entre couleur, matière et émotion visuelle."}
              </p>

              <p>
                {product.isGlitter
                  ? "Cette référence contient une touche brillante, avec lurex ou stellina, pour apporter un éclat subtil et vivant à l’ensemble."
                  : "Cette référence privilégie la profondeur pure de la teinte, sans effet brillant, pour laisser la couleur s’exprimer avec sobriété et intensité."}
              </p>
            </div>
          </div>

          <div className="grid gap-6 lg:gap-7">
            <div className="rounded-[28px] border border-[var(--border)] bg-[linear-gradient(145deg,rgba(209,15,115,0.14),rgba(217,90,47,0.10))] p-6 shadow-[var(--shadow)]">
              <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--primary)]">
                Composition
              </p>
              <p className="mt-4 text-sm leading-7 text-[var(--foreground)]/74">
                {product.material ||
                  "Mérinos superwash, nylon, et selon certaines références, une touche de lurex ou de stellina."}
              </p>
            </div>

            <div className="rounded-[28px] border border-[var(--border)] bg-[linear-gradient(145deg,rgba(209,15,115,0.14),rgba(217,90,47,0.10))] p-6 shadow-[var(--shadow)]">
              <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--primary)]">
                Métrage
              </p>
              <p className="mt-4 text-sm leading-7 text-[var(--foreground)]/74">
                {product.meterage ||
                  "Le métrage exact sera précisé prochainement pour cette référence."}
              </p>
            </div>

            <div className="rounded-[28px] border border-[var(--border)] bg-[linear-gradient(145deg,rgba(209,15,115,0.14),rgba(217,90,47,0.10))] p-6 shadow-[var(--shadow)]">
              <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--primary)]">
                Entretien
              </p>
              <p className="mt-4 text-sm leading-7 text-[var(--foreground)]/74">
                {product.careInstructions ||
                  "Lavage à la main à froid ou légèrement tiède, séchage à plat, sans sèche-linge."}
              </p>
            </div>

            <div className="rounded-[28px] border border-[var(--border)] bg-[linear-gradient(145deg,rgba(209,15,115,0.14),rgba(217,90,47,0.10))] p-6 shadow-[var(--shadow)]">
              <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
                Continuer
              </p>
              <div className="mt-4 flex flex-wrap gap-4">
                <Link
                  href="/specificites-des-produits"
                  className="rounded-full border border-[rgba(217,90,47,0.22)] bg-[rgba(217,90,47,0.12)] px-5 py-3 text-sm font-medium transition hover:border-[var(--primary)] hover:text-[var(--primary)]"
                >
                  Voir les spécificités
                </Link>
                <Link
                  href="/conseils-d-entretien"
                  className="rounded-full bg-[var(--primary)] px-5 py-3 text-sm font-medium text-white transition hover:opacity-90"
                >
                  Conseils d’entretien
                </Link>
                <Link
                  href="/conditions-generales-de-vente"
                  className="inline-flex items-center text-sm leading-7 text-[var(--foreground)]/74 underline decoration-[rgba(36,24,29,0.18)] underline-offset-4 transition hover:text-[var(--foreground)] hover:decoration-[rgba(36,24,29,0.42)]"
                >
                  Consulter les conditions de vente
                </Link>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

function RelatedProducts({
  products,
}: {
  products: Array<{
    id: string;
    name: string;
    slug: string;
    price: unknown;
    images: Array<{
      imageUrl: string;
      alt: string | null;
    }>;
  }>;
}) {
  if (!products.length) return null;

  return (
    <section className="section pt-0">
      <Container>
        <div className="mb-10 max-w-2xl">
          <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
            À découvrir aussi
          </p>
          <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
            D’autres créations dans le même univers.
          </h2>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
          {products.map((product) => {
            const image = product.images[0];

            return (
              <Link key={product.id} href={`/produit/${product.slug}`} className="group block">
                <article className="rounded-[28px] border border-[var(--border)] bg-[linear-gradient(145deg,rgba(209,15,115,0.14),rgba(217,90,47,0.10))] p-5 shadow-[var(--shadow)] transition hover:-translate-y-1 hover:shadow-[0_22px_46px_rgba(50,35,35,0.08)]">
                  <div className="overflow-hidden rounded-[22px] bg-[linear-gradient(145deg,rgba(209,15,115,0.12),rgba(217,90,47,0.10))]">
                    {image?.imageUrl ? (
                      <div className="aspect-[5/4] overflow-hidden rounded-[22px]">
                        <img
                          src={image.imageUrl}
                          alt={image.alt || product.name}
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                        />
                      </div>
                    ) : (
                      <div className="aspect-[5/4] rounded-[22px] bg-[linear-gradient(145deg,rgba(209,15,115,0.18),rgba(217,90,47,0.10),rgba(244,215,231,0.90))]" />
                    )}
                  </div>

                  <h3 className="mt-5 text-2xl leading-tight">{product.name}</h3>
                  <p className="mt-3 text-lg font-semibold text-[var(--primary)] drop-shadow-[0_8px_18px_rgba(255,63,142,0.10)]">
                    {formatPrice(String(product.price))}
                  </p>
                </article>
              </Link>
            );
          })}
        </div>
      </Container>
    </section>
  );
}